import './lesson-article.less';
import MDEditor from '@uiw/react-md-editor';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import type { Components } from 'react-markdown';
import { AiTaPanel } from './ai-ta-panel';
import { remarkAiChat } from './remark-ai-chat';
import { preprocessLessonMarkdown } from './markdown-preprocessors';
import { createContext, useContext, useMemo } from 'react';
import rehypeRaw from 'rehype-raw';

// 课程上下文，用于 AI 会话管理
export interface CourseContext {
  courseId: string;
  chapterId: string;
  courseTitle?: string;
  chapterTitle?: string;
  chapterIntro?: string;
  chapterObjective?: string;
}

const CourseContextValue = createContext<CourseContext | null>(null);

export const useCourseContext = () => useContext(CourseContextValue);

interface LessonArticleProps {
  lessonLabel: string; // e.g. "Lesson 2.4"
  readTime: string; // e.g. "25 min read"
  title: string;
  subtitle: string;
  content: string; // Markdown content
  courseContext?: CourseContext;
}

const articleComponents: Components = {
  li: ({ children }) => (
    <li>
      <span className="lesson-article__li-dot" />
      <span>{children}</span>
    </li>
  ),
  blockquote: ({ children }) => <blockquote className="lesson-article__blockquote">{children}</blockquote>,
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return <code className={`lesson-article__code-block ${className ?? ''}`}>{children}</code>;
    }
    return <code>{children}</code>;
  },
  img: ({ src, alt }) => (
    <figure className="lesson-article__figure">
      <img alt={alt ?? ''} src={src} />
      {alt && <figcaption className="lesson-article__figcaption">{alt}</figcaption>}
    </figure>
  ),
  table: ({ children }) => (
    <div className="lesson-article__table-wrapper">
      <table>{children}</table>
    </div>
  ),
} as Components;

// 扩展 Components 类型以支持自定义 ai-chat 标签
interface ExtendedComponents extends Components {
  'ai-chat'?: (config: { system?: string; ai?: string; goal?: string; criteria?: string }) => JSX.Element;
}

const extendedComponents: ExtendedComponents = {
  ...articleComponents,
  'ai-chat': ({ system, ai, goal, criteria }) => {
    const context = useCourseContext();
    if (!context) return <div />;
    return (
      <AiTaPanel
        ai={ai}
        chapterId={context.chapterId}
        chapterIntro={context.chapterIntro}
        chapterObjective={context.chapterObjective}
        chapterTitle={context.chapterTitle}
        courseId={context.courseId}
        courseTitle={context.courseTitle}
        criteria={criteria}
        goal={goal}
        system={system}
      />
    );
  },
};

export function LessonArticle({ lessonLabel, readTime, title, subtitle, content, courseContext }: LessonArticleProps) {
  // 预处理：应用所有 Markdown 预处理器
  const processedContent = preprocessLessonMarkdown(content);

  // 自定义 MDEditor 预览渲染器，复用 ReactMarkdown + remark 插件 + AiTaPanel
  const mdPreview = useMemo(
    () => (
      <Markdown components={extendedComponents} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm, remarkDirective, remarkAiChat]}>
        {processedContent}
      </Markdown>
    ),
    [processedContent],
  );

  return (
    <CourseContextValue.Provider value={courseContext ?? null}>
      <article className="lesson-article">
        {/* ── Header: badge + read time + title + subtitle ── */}
        <header className="lesson-article__header">
          <div className="lesson-article__meta">
            <span className="lesson-article__badge">{lessonLabel}</span>
            <span className="lesson-article__readtime">{readTime}</span>
          </div>
          <h1 className="lesson-article__title">{title}</h1>
          <p className="lesson-article__subtitle">{subtitle}</p>
        </header>

        {/* ── Separator ── */}
        <hr className="lesson-article__separator" />

        {/* ── Markdown content via MDEditor preview ── */}
        <div className="lesson-article__content">
          <MDEditor
            components={{
              preview: () => mdPreview,
            }}
            hideToolbar
            preview="preview"
            style={{ height: 'auto' }}
            value={processedContent}
          />
        </div>
      </article>
    </CourseContextValue.Provider>
  );
}
